import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AuthenticationService } from '../../auth/authentication.service';
import { AjsResponse, wrapAjs } from '../ajs-response';
import { DownloadType, JobExecution } from './import-job-executions-api.types';

/**
 * ImportJobExecutionsApiService (Angular 18) — Import Job Execution
 * microservice (job executions, validations, errors, file uploads).
 *
 * Replaces apps/b2b-ui/common/services/api/import/importJobExecutionsApi.service.js.
 *
 * Endpoint base hardcoded to /service/import/api (legacy provider's
 * setEndpoint() value). Internal sub-paths:
 *   /service/import/api/job/:jobId/executions
 *   /service/import/api/jobexecution/...
 *
 * Legacy quirks preserved:
 *   - `x-tenant-id` header captured ONCE at construction from the `user`
 *     cookie. Matches the legacy provider's $get factory closure.
 *   - `uploadCsv(url, file)` PUTs to an S3 presigned URL with `Content-Type:
 *     text/csv` and `x-amz-server-side-encryption: AES256` — NO `x-tenant-id`
 *     header (S3 rejects unknown signed headers).
 *   - `getDownloadUrl(id, type)` returns a `string` synchronously (used for
 *     anchor hrefs in templates) — does NOT return a Promise.
 */
const BASE = '/service/import/api';
const JOBS_BASE = `${BASE}/job`;
const JOB_EXECUTIONS_BASE = `${BASE}/jobexecution`;

@Injectable({ providedIn: 'root' })
export class ImportJobExecutionsApiService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthenticationService);

  private readonly tenantId: string | undefined = this.readTenantId();

  private readTenantId(): string | undefined {
    const userCookie = this.auth.getCookie<{ tenantId?: string }>('user') ?? {};
    return userCookie.tenantId;
  }

  private get tenantHeaders(): HttpHeaders {
    return new HttpHeaders({ 'x-tenant-id': this.tenantId ?? '' });
  }

  list(
    jobId: number | string,
    page?: number | null,
    size?: number | null,
    sort?: string | null,
  ): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${JOBS_BASE}/${jobId}/executions`, {
        headers: this.tenantHeaders,
        params: {
          page: page ?? '',
          size: size ?? '',
          sort: sort ?? '',
        },
      }),
    );
  }

  create(jobId: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.post<unknown>(JOB_EXECUTIONS_BASE, { jobId }, { headers: this.tenantHeaders }));
  }

  get(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${JOB_EXECUTIONS_BASE}/${id}`, { headers: this.tenantHeaders }));
  }

  update(jobExecution: JobExecution & { id: number | string }): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(`${JOB_EXECUTIONS_BASE}/${jobExecution.id}`, jobExecution, {
        headers: this.tenantHeaders,
      }),
    );
  }

  presign(id: number | string, filename: string): Promise<AjsResponse<string>> {
    // The presign endpoint returns the S3 URL as a bare (unquoted) string.
    // `responseType: 'text'` is required — the default 'json' parsing rejects
    // a non-JSON body with an HttpErrorResponse even on a 200, which the legacy
    // $http silently tolerated by falling back to the raw string.
    return wrapAjs(
      this.http.post(`${JOB_EXECUTIONS_BASE}/${id}/presign/${filename}`, null, {
        headers: this.tenantHeaders,
        responseType: 'text',
      }),
    );
  }

  /**
   * Upload a CSV file directly to an AWS S3 pre-signed URL.
   * Intentionally does NOT include the `x-tenant-id` header — S3 rejects
   * additional signed headers that weren't part of the presigned signature.
   */
  uploadCsv(url: string, file: Blob | File): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.put<unknown>(url, file, {
        headers: new HttpHeaders({
          'Content-Type': 'text/csv',
          'x-amz-server-side-encryption': 'AES256',
        }),
      }),
    );
  }

  /** Synchronous: returns the raw URL string used as an anchor href. */
  getDownloadUrl(id: number | string, type: DownloadType): string {
    return `${JOB_EXECUTIONS_BASE}/${id}/download/${type}`;
  }

  stage(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(`${JOB_EXECUTIONS_BASE}/${id}/stage`, null, {
        headers: this.tenantHeaders,
      }),
    );
  }

  validate(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(`${JOB_EXECUTIONS_BASE}/${id}/validate`, null, {
        headers: this.tenantHeaders,
      }),
    );
  }

  validateExternally(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(`${JOB_EXECUTIONS_BASE}/${id}/validate_external`, null, {
        headers: this.tenantHeaders,
      }),
    );
  }

  importData(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(`${JOB_EXECUTIONS_BASE}/${id}/import`, null, {
        headers: this.tenantHeaders,
      }),
    );
  }

  errors(
    id: number | string,
    page?: number | null,
    size?: number | null,
    sort?: string | null,
  ): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${JOB_EXECUTIONS_BASE}/${id}/errors`, {
        headers: this.tenantHeaders,
        params: {
          page: page ?? '',
          size: size ?? '',
          sort: sort ?? '',
        },
      }),
    );
  }

  available(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(`${JOB_EXECUTIONS_BASE}/${id}/available`, {
        headers: this.tenantHeaders,
      }),
    );
  }

  remove(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.delete<unknown>(`${JOB_EXECUTIONS_BASE}/${id}`, {
        headers: this.tenantHeaders,
      }),
    );
  }

  stop(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(`${JOB_EXECUTIONS_BASE}/${id}/stop`, null, {
        headers: this.tenantHeaders,
      }),
    );
  }
}
